import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { FirebaseContext } from "../firebase"
import { callCloudFunction } from "../helpers/cloudFunctionRequests"
import { graphqlRequest } from "../helpers/graphqlRequest"
import { LogError } from "../helpers/logger"
import { getUserToken } from "../hooks/tokenManager/useTokenManager"

const AuthContext = createContext(null)
const AUTH_BOOTSTRAP_SOURCE = {
  RESTORE: "restore",
  LOGIN: "login",
}
const MFA_REQUIRED_ERROR_CODE = "auth/multi-factor-auth-required"
const AUTO_LOGIN_FAILURE_MESSAGE =
  "We couldn't restore your session automatically.\nPlease sign in again."
const MANUAL_LOGIN_FAILURE_MESSAGE =
  "We couldn't complete sign in right now.\nPlease try again."

const getSessionBootstrapErrorMessage = (bootstrapSource) => {
  return bootstrapSource === AUTH_BOOTSTRAP_SOURCE.LOGIN
    ? MANUAL_LOGIN_FAILURE_MESSAGE
    : AUTO_LOGIN_FAILURE_MESSAGE
}

export function AuthProvider({ children }) {
  const firebase = useContext(FirebaseContext)

  const [authStateReady, setAuthStateReady] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [errSession, setErrSession] = useState("")
  const [authUser, setAuthUser] = useState(null)
  const [authUserVersion, setAuthUserVersion] = useState(0)
  const [session, setSession] = useState(null)

  const sessionDocUnsubscribeRef = useRef(null)
  const authRequestGenerationRef = useRef(0)
  const pendingManualLoginRef = useRef(false)

  const clearSessionListener = useCallback(() => {
    if (sessionDocUnsubscribeRef.current) {
      sessionDocUnsubscribeRef.current()
      sessionDocUnsubscribeRef.current = null
    }
  }, [])

  const signInWithEmailAndPassword = useCallback(
    async (email, password) => {
      pendingManualLoginRef.current = true

      try {
        return await firebase.auth.signInWithEmailAndPassword(email, password)
      } catch (error) {
        // Keep login context for MFA-required flows. The first-factor sign-in
        // rejects by design, and the completed MFA sign-in resolves later via
        // onAuthStateChanged.
        if (error?.code !== MFA_REQUIRED_ERROR_CODE) {
          pendingManualLoginRef.current = false
        }
        throw error
      }
    },
    [firebase.auth],
  )

  const signOut = useCallback(
    (options = {}) => {
      pendingManualLoginRef.current = false
      return firebase.signOut(options)
    },
    [firebase],
  )

  const sendPasswordResetEmail = useCallback(async (email) => {
    const trimmedEmail = String(email || "").trim()

    if (!trimmedEmail) {
      throw new Error("Email is required")
    }

    const graphqlMutation = `
        mutation createPasswordResetEmail(
          $email: String!
        ) {
          createPasswordResetNotification(
            email: $email
          ) {
            status
            error
          }
        }
      `

    const data = await graphqlRequest({
      query: graphqlMutation,
      variables: {
        email: trimmedEmail,
      },
      fallbackErrorMessage:
        "Unable to send reset email right now. Please try again.",
    })

    const mutationResponse = data?.createPasswordResetNotification

    if (mutationResponse?.status !== "success") {
      throw new Error("Unable to send reset email right now. Please try again.")
    }

    if (mutationResponse.error) {
      throw new Error(mutationResponse.error)
    }

    return mutationResponse
  }, [])
  
  const sendEmailVerificationNotification = useCallback(async (authToken) => {
    const email = authUser?.email || session?.user?.staff_email_address
    const userId = session?.user?.staff_id || authUser?.uid
    const enterpriseId = session?.user?.enterprise_id
    const organizationId = session?.user?.organization_id

    if (!authToken) {
      throw new Error("token is required")
    }

    if (!email) {
      throw new Error("email is required")
    }

    if (!userId) {
      throw new Error("user_id is required")
    }

    if (!enterpriseId) {
      throw new Error("enterprise_id is required")
    }

    if (!organizationId) {
      throw new Error("organization_id is required")
    }

    const graphqlMutation = `
      mutation verifyEmail(
        $email: String!
        $user_id: String!
        $enterprise_id: String!
        $organization_id: String!
      ) {
        createEmailVerificationNotification(
          email: $email
          user_id: $user_id
          enterprise_id: $enterprise_id
          organization_id: $organization_id
        ) {
          status
          error
        }
      }
    `

    const data = await graphqlRequest({
      query: graphqlMutation,
      variables: {
        email,
        user_id: userId,
        enterprise_id: enterpriseId,
        organization_id: organizationId,
      },
      authToken,
      fallbackErrorMessage:
        "Unable to send verification email right now. Please try again.",
    })

    const mutationResponse = data?.createEmailVerificationNotification

    if (!mutationResponse) {
      throw new Error(
        "Unable to send verification email right now. Please try again.",
      )
    }

    if (mutationResponse.error) {
      throw new Error(mutationResponse.error)
    }

    if (mutationResponse.status !== "success") {
      throw new Error(
        "Unable to send verification email right now. Please try again.",
      )
    }

    return mutationResponse
  }, [authUser?.email, authUser?.uid, session])

  const reloadAuthUser = useCallback(async () => {
    if (!firebase.auth.currentUser) return null

    await firebase.auth.currentUser.reload()
    //Bump the auth user version to trigger re-renders in components that depend on it.
    setAuthUserVersion((currentVersion) => currentVersion + 1)
    return firebase.auth.currentUser
  }, [firebase.auth])

  const getAuthIdToken = useCallback(
    async (user = null) => {
      const authUserToUse = user || firebase.auth.currentUser
      if (!authUserToUse) return null

      return authUserToUse.getIdToken()
    },
    [firebase.auth],
  )

  useEffect(() => {
    const fetchSessionData = async (
      nextAuthUser,
      requestGeneration,
      bootstrapSource,
    ) => {
      const isStaleRequest = () => {
        return (
          authRequestGenerationRef.current !== requestGeneration ||
          firebase.auth.currentUser?.uid !== nextAuthUser.uid
        )
      }

      try {
        // Load app token by exchanging Firebase identity for the app session token.
        const newToken = await getUserToken(nextAuthUser)

        if (isStaleRequest()) {
          return
        }

        if (newToken === null) {
          setErrSession(getSessionBootstrapErrorMessage(bootstrapSource))
          setSession(null)
          setFetching(false)
          return
        }

        const getSession = await callCloudFunction(
          "setSession",
          {
            uid: nextAuthUser.uid,
            token: newToken,
          },
          newToken,
        )

        if (isStaleRequest()) {
          return
        }

        const sessionId = getSession.data.sessionId
        const sessionDocRef = firebase.db.doc(`sessions/${sessionId}`)

        clearSessionListener()
        sessionDocUnsubscribeRef.current = sessionDocRef.onSnapshot(
          (snapshot) => {
            if (isStaleRequest()) {
              return
            }

            const userData = snapshot?.data()?.user || undefined

            if (userData === undefined) {
              LogError("No user data found for current user")
              setErrSession(getSessionBootstrapErrorMessage(bootstrapSource))
              setFetching(false)
              setAuthUser(null)
              setSession(null)
              return
            }

            window.dataLayer.push({
              event: "login",
              staff_id: snapshot?.data()?.user?.staff_id,
              staff_membership_id: "",
              organization_id: snapshot?.data()?.user?.organization_id,
              enterprise_id: snapshot?.data()?.user?.enterprise_id,
              user_role: snapshot?.data()?.user?.staff_role,
            })

            setAuthUser(nextAuthUser)
            setSession(snapshot?.data())
            setFetching(false)
          },
          (error) => {
            if (isStaleRequest()) {
              return
            }
            LogError("Error fetching document:", error)
            setFetching(false)
          },
        )
      } catch (error) {
        if (isStaleRequest()) {
          return
        }

        setErrSession(getSessionBootstrapErrorMessage(bootstrapSource))

        if (error.response) {
          const { status } = error.response
          if (status === 401) {
            setErrSession(
              "Your account is not connected to any active Organization Memberships. If you need assistance, please contact support@myrecoverylink.com",
            )
          }
        }
        LogError("Error when fetching Session Data:", error)
        setFetching(false)
      }
    }

    const unsubscribe = firebase.auth.onAuthStateChanged((nextAuthUser) => {
      const requestGeneration = authRequestGenerationRef.current + 1
      authRequestGenerationRef.current = requestGeneration

      clearSessionListener()
      setAuthStateReady(true)

      if (nextAuthUser) {
        window.appUser = {
          id: nextAuthUser.uid,
          email: nextAuthUser.email,
          organization_id: null,
          enterprise_id: null,
          participant_id: null,
        }
      } else {
        window.appUser = null
      }

      if (nextAuthUser?.uid) {
        const bootstrapSource = pendingManualLoginRef.current
          ? AUTH_BOOTSTRAP_SOURCE.LOGIN
          : AUTH_BOOTSTRAP_SOURCE.RESTORE
        pendingManualLoginRef.current = false

        // Immediately clear stale app auth data from prior user while the new
        // session/token bootstrap is in-flight.
        setFetching(true)
        setSession(null)

        setAuthUser(nextAuthUser)
        //Bump the auth user version to trigger re-renders in components that depend on it.
        setAuthUserVersion((currentVersion) => currentVersion + 1)
        fetchSessionData(nextAuthUser, requestGeneration, bootstrapSource)
      } else {
        // Keep pending manual-login context for MFA-required flows until the
        // completed resolver sign-in emits an authenticated auth state.
        setFetching(false)
        setAuthUser(null)
        //Bump the auth user version to trigger re-renders in components that depend on it.
        setAuthUserVersion((currentVersion) => currentVersion + 1)
        setSession(null)
      }
    })

    return () => {
      clearSessionListener()
      unsubscribe()
    }
  }, [clearSessionListener, firebase.auth, firebase.db])

  const value = useMemo(
    () => ({
      auth: firebase.auth,
      authStateReady,
      fetching,
      errSession,
      setErrSession,
      authUser,
      authUserVersion,
      session,
      signInWithEmailAndPassword,
      signOut,
      sendPasswordResetEmail,
      sendEmailVerificationNotification,
      reloadAuthUser,
      getAuthIdToken,
    }),
    [
      firebase.auth,
      authStateReady,
      fetching,
      errSession,
      authUser,
      authUserVersion,
      session,
      signInWithEmailAndPassword,
      signOut,
      sendPasswordResetEmail,
      sendEmailVerificationNotification,
      reloadAuthUser,
      getAuthIdToken,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }

  return context
}

export default AuthContext
